import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, InputAdornment, TablePagination,
  Chip, IconButton, Grid, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { cardManagementService } from '../../services/cardManagementService';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../accounting/shared/StatsCard';
import { useTranslation } from 'react-i18next';

const formatDateGMT3 = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const gmt3 = new Date(d.getTime() + 3 * 60 * 60 * 1000);
  return gmt3.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const DeletedCards: React.FC = () => {
  const { t, i18n } = useTranslation(['cardManagement', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const cm = (key: string, defaultValue: string) => t(key, { ns: 'cardManagement', defaultValue });
  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId || '';
  const navigate = useNavigate();

  const [cards, setCards] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [loading, setLoading] = useState(false);

  const [restoreTarget, setRestoreTarget] = useState<any>(null);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [hardDeleteTarget, setHardDeleteTarget] = useState<any>(null);
  const [hardDeleteModalOpen, setHardDeleteModalOpen] = useState(false);
  const [hardDeleteLoading, setHardDeleteLoading] = useState(false);
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadCards = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await cardManagementService.getDeletedCards(companyId, {
        page: page + 1,
        pageSize,
        search: searchDebounced || undefined,
      });
      setCards(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch {
      setCards([]);
    }
    setLoading(false);
  }, [companyId, page, pageSize, searchDebounced]);

  useEffect(() => { loadCards(); }, [loadCards]);

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoreLoading(true);
    try {
      await cardManagementService.restoreCard({ Id: restoreTarget.id, CompanyId: companyId });
      setSnackbar({ open: true, message: cm('deleted.messages.restored', 'Card restored'), severity: 'success' });
      setRestoreModalOpen(false);
      setRestoreTarget(null);
      loadCards();
    } catch {
      setSnackbar({ open: true, message: cm('deleted.messages.restoreError', 'An error occurred while restoring card'), severity: 'error' });
    }
    setRestoreLoading(false);
  };

  const handleHardDelete = async () => {
    if (!hardDeleteTarget) return;
    setHardDeleteLoading(true);
    try {
      await cardManagementService.hardDeleteCard({ Id: hardDeleteTarget.id, CompanyId: companyId });
      setSnackbar({ open: true, message: cm('deleted.messages.deleted', 'Card permanently deleted'), severity: 'success' });
      setHardDeleteModalOpen(false);
      setHardDeleteTarget(null);
      setHardDeleteConfirmText('');
      loadCards();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || cm('deleted.messages.deleteError', 'An error occurred while permanently deleting card'), severity: 'error' });
    }
    setHardDeleteLoading(false);
  };

  const columns = useMemo<DataTableColumn<any>[]>(() => [
    {
      id: 'rowNumber',
      label: cm('deleted.table.no', 'No'),
      width: '60px',
      render: (_row: any, index: number) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {page * pageSize + index + 1}
        </Box>
      ),
    },
    {
      id: 'cardNumber',
      label: cm('deleted.table.cardNo', 'Card No'),
      sortable: true,
      render: (row: any) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {row.cardNumber}
        </Typography>
      ),
    },
    {
      id: 'cardUID',
      label: cm('deleted.table.cardUid', 'Card UID'),
      sortable: true,
      render: (row: any) => <>{row.cardUID || '—'}</>,
    },
    {
      id: 'cardType',
      label: cm('deleted.table.cardType', 'Card Type'),
      render: (row: any) => (
        <Chip label={row.cardTypeName || '—'} size="small"
          sx={{ bgcolor: row.cardTypeColor ? `${row.cardTypeColor}20` : undefined, color: row.cardTypeColor }} />
      ),
    },
    {
      id: 'wallets',
      label: cm('deleted.table.wallets', 'Wallets'),
      render: (row: any) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(row.wallets || []).map((w: any) => (
            <Chip key={w.walletId} label={w.walletName} size="small" variant="outlined" />
          ))}
        </Box>
      ),
    },
    {
      id: 'accountName',
      label: cm('deleted.table.owner', 'Card Owner'),
      sortable: true,
      // Sahip cari ya da personel olabilir; ownerName backend'de türe göre çözülür.
      render: (row: any) => <>{row.ownerName || row.accountName || '—'}</>,
    },
    {
      id: 'deletedAt',
      label: cm('deleted.table.deletedAt', 'Deleted At'),
      render: (row: any) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {formatDateGMT3(row.deletedAt)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: cm('deleted.table.actions', 'Actions'),
      align: 'right',
      render: (row: any) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={cm('common.restore', 'Restore')}>
            <IconButton
              size="small"
              color="success"
              onClick={(e) => { e.stopPropagation(); setRestoreTarget(row); setRestoreModalOpen(true); }}
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={cm('deleted.hardDelete', 'Hard Delete')}>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => { e.stopPropagation(); setHardDeleteTarget(row); setHardDeleteModalOpen(true); setHardDeleteConfirmText(''); }}
            >
              <DeleteForeverIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ], [page, pageSize]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/kart-yonetimi/kartlar')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <DeleteSweepIcon sx={{ fontSize: 32, mr: 1.5, color: 'error.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {cm('deleted.title', 'Deleted Cards')}
          </Typography>
        </Box>
      </Box>

      <StatsCardsContainer loading={loading && cards.length === 0}>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCardItem
            icon={<DeleteSweepIcon />}
            color={STATS_COLORS.red}
            title={`${totalCount}`}
            subtitle={cm('deleted.stats.totalDeletedCards', 'Total Deleted Cards')}
          />
        </Grid>
      </StatsCardsContainer>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={9}>
            <TextField
              fullWidth
              placeholder={cm('deleted.filters.searchPlaceholder', 'Search Card No, UID or Account...')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton onClick={loadCards} title={cm('common.refresh', 'Refresh')}>
              <RefreshIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      <AccountingDataTable<any>
        columns={columns}
        rows={cards}
        getRowId={(row) => row.id}
        loading={loading}
        pagination={false}
        defaultSortField="deletedAt"
        defaultSortDirection="desc"
        emptyMessage={cm('common.noDataDeletedCards', 'No deleted cards found.')}
        emptyIcon={<DeleteSweepIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel={cm('deleted.tableAriaLabel', 'deleted cards table')}
      />
      <Paper sx={{ borderRadius: 0, borderBottomLeftRadius: 8, borderBottomRightRadius: 8, mt: -0.5 }}>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={cm('common.rowsPerPage', 'Rows per page:')}
          labelDisplayedRows={({ from, to, count }) => isEnglish ? `${from}–${to} of ${count}` : `${count} kayıttan ${from}–${to} arası`}
        />
      </Paper>

      <Dialog open={restoreModalOpen} onClose={() => { setRestoreModalOpen(false); setRestoreTarget(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>{cm('deleted.restoreTitle', 'Restore Card')}</DialogTitle>
        <DialogContent>
          <Typography>
            {cm('deleted.restoreQuestion', 'Are you sure you want to restore card')} <strong>{restoreTarget?.cardNumber}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRestoreModalOpen(false); setRestoreTarget(null); }}>{cm('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleRestore} variant="contained" color="success" disabled={restoreLoading}>
            {restoreLoading ? cm('common.loading', 'Loading...') : cm('common.restore', 'Restore')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hardDeleteModalOpen}
        onClose={() => { setHardDeleteModalOpen(false); setHardDeleteTarget(null); setHardDeleteConfirmText(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmberIcon color="error" />
          {cm('deleted.hardDeleteTitle', 'Hard Delete')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" icon={<DeleteForeverIcon />} sx={{ mb: 2 }}>
            <strong>{cm('deleted.notUndoable', 'This action cannot be undone!')}</strong> {cm('deleted.hardDeleteDescription', 'The card will be permanently deleted from database and cannot be recovered.')}
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {cm('deleted.hardDeleteQuestion', 'Are you sure you want to permanently delete card')} <strong>{hardDeleteTarget?.cardNumber}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {cm('deleted.confirmTypePrefix', 'To confirm, type')} <strong>{cm('deleted.confirmKeyword', 'DELETE')}</strong> {cm('deleted.confirmTypeSuffix', 'in the field below:')}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={cm('deleted.confirmKeyword', 'DELETE')}
            value={hardDeleteConfirmText}
            onChange={(e) => setHardDeleteConfirmText(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': { borderColor: 'error.main' },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHardDeleteModalOpen(false); setHardDeleteTarget(null); setHardDeleteConfirmText(''); }}>
            {cm('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleHardDelete}
            variant="contained"
            color="error"
            disabled={hardDeleteLoading || hardDeleteConfirmText !== cm('deleted.confirmKeyword', 'DELETE')}
            startIcon={<DeleteForeverIcon />}
          >
            {hardDeleteLoading ? cm('common.deleting', 'Deleting...') : cm('deleted.deletePermanently', 'Delete Permanently')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default DeletedCards;
